// ============================================
// DATEI 2: useCredentials.ts
// Pfad: src/features/kommunikation/hooks/useCredentials.ts
// ============================================
//
// ZUGANGSDATEN AUS DATEV (05.10.2026): Zusätzlich zu den Zugangsdaten werden
// die Kunden geladen, deren Zugangsdaten aus DATEV kommen, sowie der jüngste
// E-Mail-Änderungsauftrag je Mitarbeiter. Beides ist für die Anzeige gedacht;
// schlägt das Laden fehl, bleibt die Liste trotzdem nutzbar. Gespeichert wird
// über credentialsService.speichereMitDatev.

import { useState, useEffect, useCallback } from 'react';
import {
  CommunicationCredential,
  CredentialImportResult,
  CredentialSpeicherErgebnis,
  EmailAuftragStand
} from '../types';
import { credentialsService } from '../services/CredentialsService';

export const useCredentials = () => {
  const [credentials, setCredentials] = useState<CommunicationCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [datevKunden, setDatevKunden] = useState<Set<string>>(new Set());
  const [emailAuftraege, setEmailAuftraege] = useState<Map<string, EmailAuftragStand>>(new Map());

  /**
   * Lädt alle Credentials
   */
  const loadCredentials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await credentialsService.getAllCredentials();
      setCredentials(data);

      // DATEV-Stand für die Anzeige. Fehler hier blockieren die Liste nicht.
      try {
        const [kunden, auftraege] = await Promise.all([
          credentialsService.ladeDatevKunden(),
          credentialsService.ladeEmailAuftraege()
        ]);
        setDatevKunden(kunden);
        setEmailAuftraege(auftraege);
      } catch (datevErr) {
        console.error('Error loading DATEV state:', datevErr);
      }
      
    } catch (err) {
      console.error('Error loading credentials:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Credentials');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Lädt Daten initial
   */
  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  /**
   * Importiert Credentials aus CSV
   */
  const importFromCSV = useCallback(async (
    file: File,
    uploadedByEmail: string
  ): Promise<CredentialImportResult> => {
    try {
      setError(null);
      
      const result = await credentialsService.importFromCSV(file, uploadedByEmail);
      
      if (result.success) {
        await loadCredentials();
      }
      
      return result;
      
    } catch (err) {
      console.error('Error importing CSV:', err);
      const errorMessage = err instanceof Error ? err.message : 'Import fehlgeschlagen';
      setError(errorMessage);
      
      return {
        success: false,
        imported: 0,
        updated: 0,
        skipped: 0,
        errors: [errorMessage]
      };
    }
  }, [loadCredentials]);

  /**
   * Legt ein neues Credential an oder aktualisiert ein bestehendes
   * (per UNIQUE-Key beraternummer + mandantennummer + personalnummer).
   * Bei Kunden mit Zugangsdaten aus DATEV wird eine neue E-Mail-Adresse
   * zusätzlich an DATEV übermittelt (siehe CredentialsService).
   */
  const addCredential = useCallback(async (
    credential: Omit<CommunicationCredential, 'id' | 'created_at' | 'updated_at'>
  ): Promise<CredentialSpeicherErgebnis> => {
    try {
      setError(null);

      const result = await credentialsService.speichereMitDatev(credential);

      if (result.success) {
        await loadCredentials();
      } else {
        setError(result.error || 'Speichern fehlgeschlagen');
      }

      return result;
    } catch (err) {
      console.error('Error adding credential:', err);
      const errorMessage = err instanceof Error ? err.message : 'Speichern fehlgeschlagen';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [loadCredentials]);

  /**
   * Deaktiviert ein Credential
   */
  const deactivateCredential = useCallback(async (id: string): Promise<boolean> => {
    try {
      const result = await credentialsService.deactivateCredential(id);
      
      if (result.success) {
        await loadCredentials();
        return true;
      } else {
        setError(result.error || 'Deaktivieren fehlgeschlagen');
        return false;
      }
    } catch (err) {
      console.error('Error deactivating credential:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Deaktivieren');
      return false;
    }
  }, [loadCredentials]);

  return {
    credentials,
    loading,
    error,
    loadCredentials,
    importFromCSV,
    addCredential,
    deactivateCredential,
    datevKunden,
    emailAuftraege
  };
};
